// src/pages/services/ServiceDetailsPage.jsx
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  Package, 
  DollarSign, 
  FileText, 
  Calendar,
  TrendingUp,
  AlertTriangle,
  Settings,
  Copy,
  ExternalLink,
  Loader2,
  RefreshCw,
  BarChart3
} from 'lucide-react';
import toast from 'react-hot-toast';

// Import your API service - adjust path as needed
import { serviceService } from '../../services/';

const ServiceDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch service details
  const { 
    data: service, 
    isLoading: serviceLoading, 
    error: serviceError,
    refetch: refetchService 
  } = useQuery(
    ['service', id],
    () => serviceService.getById(parseInt(id)),
    {
      enabled: !!id,
      onError: (error) => {
        console.error('Error fetching service:', error);
        if (error.response?.status === 404) {
          toast.error('Serviciul nu a fost găsit');
          navigate('/services');
        } else {
          toast.error('Eroare la încărcarea serviciului');
        }
      }
    }
  );

  // Delete service mutation
  const deleteMutation = useMutation(
    () => serviceService.delete(parseInt(id)),
    {
      onSuccess: () => {
        toast.success('Serviciul a fost șters cu succes');
        queryClient.invalidateQueries('services');
        navigate('/services');
      },
      onError: (error) => {
        console.error('Error deleting service:', error);
        if (error.response?.status === 400) {
          toast.error('Nu se poate șterge serviciul. Este folosit în contracte existente.');
        } else {
          toast.error('Eroare la ștergerea serviciului');
        }
      }
    }
  );

  const handleEdit = () => {
    navigate(`/services/${id}/edit`);
  };

  const handleDelete = () => {
    deleteMutation.mutate();
    setShowDeleteConfirm(false);
  };

  const handleCopyCode = () => {
    if (service?.CpvCode) {
      navigator.clipboard.writeText(service.CpvCode);
      toast.success('Cod CPV copiat în clipboard');
    }
  };

  const formatPrice = (price, currency) => {
    return new Intl.NumberFormat('ro-RO', {
      style: 'currency',
      currency: currency || 'RON',
      minimumFractionDigits: 2
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUsageColor = (count) => {
    if (count === 0) return 'text-gray-500';
    if (count <= 5) return 'text-green-600';
    if (count <= 15) return 'text-blue-600';
    return 'text-purple-600';
  };

  const getUsageBadgeColor = (count) => {
    if (count === 0) return 'bg-gray-100 text-gray-800';
    if (count <= 5) return 'bg-green-100 text-green-800';
    if (count <= 15) return 'bg-blue-100 text-blue-800';
    return 'bg-purple-100 text-purple-800';
  };

  if (serviceLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="animate-pulse">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-4">
                  <div className="w-8 h-8 bg-gray-200 rounded"></div>
                  <div className="h-8 bg-gray-200 rounded w-64"></div>
                </div>
                <div className="flex space-x-2">
                  <div className="w-24 h-10 bg-gray-200 rounded"></div>
                  <div className="w-24 h-10 bg-gray-200 rounded"></div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                  <div className="h-32 bg-gray-200 rounded-lg"></div>
                  <div className="h-48 bg-gray-200 rounded-lg"></div>
                </div>
                <div className="space-y-6">
                  <div className="h-64 bg-gray-200 rounded-lg"></div>
                  <div className="h-32 bg-gray-200 rounded-lg"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (serviceError || !service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Serviciu Negăsit
            </h2>
            <p className="text-gray-600 mb-6">
              Serviciul solicitat nu a fost găsit sau a apărut o eroare.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => refetchService()}
                className="w-full bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors flex items-center justify-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Încearcă din nou
              </button>
              <button
                onClick={() => navigate('/services')}
                className="w-full bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                Înapoi la servicii
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/services')}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <ArrowLeft className="w-6 h-6" />
                </button>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <Package className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">{service.Name}</h1>
                    <p className="text-sm text-gray-600">Detalii serviciu</p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleEdit}
                  className="bg-secondary-500 text-white px-4 py-2 rounded-lg hover:bg-secondary-600 transition-colors flex items-center"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editează
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Șterge
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Service Overview */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Prezentare Generală
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <DollarSign className="w-8 h-8 text-green-600 mr-3" />
                    <div>
                      <p className="text-sm text-green-600 font-medium">Preț</p>
                      <p className="text-2xl font-bold text-green-900">
                        {formatPrice(service.Price, service.Currency)}
                      </p>
                      <p className="text-sm text-green-600">per {service.Unit}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <TrendingUp className="w-8 h-8 text-blue-600 mr-3" />
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Utilizări</p>
                      <p className={`text-2xl font-bold ${getUsageColor(service.UsageCount)}`}>
                        {service.UsageCount}
                      </p>
                      <p className="text-sm text-blue-600">contracte</p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Settings className="w-8 h-8 text-purple-600 mr-3" />
                    <div>
                      <p className="text-sm text-purple-600 font-medium">Monedă</p>
                      <p className="text-2xl font-bold text-purple-900">
                        {service.Currency}
                      </p>
                      <p className="text-sm text-purple-600">valută</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-3">Descriere</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 leading-relaxed">
                    {service.Description || 'Nu există descriere disponibilă pentru acest serviciu.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Technical Details */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Detalii Tehnice
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unitate de măsură
                  </label>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-gray-900 font-medium">{service.Unit}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cod CPV
                  </label>
                  <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                    <p className="text-gray-900 font-medium">
                      {service.CpvCode || 'Nu este specificat'}
                    </p>
                    {service.CpvCode && (
                      <button
                        onClick={handleCopyCode}
                        className="text-primary-500 hover:text-primary-600 transition-colors"
                        title="Copiază cod CPV"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Usage Analytics Placeholder */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  Analiză Utilizare
                </h2>
                <BarChart3 className="w-5 h-5 text-gray-400" />
              </div>
              
              <div className="bg-gray-50 rounded-lg p-8 text-center">
                <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 font-medium mb-2">Grafic Utilizare</p>
                <p className="text-sm text-gray-500">
                  Aici va fi afișat graficul cu utilizarea serviciului în timp
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Statistici Rapide
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status utilizare</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUsageBadgeColor(service.UsageCount)}`}>
                    {service.UsageCount === 0 ? 'Nefolosit' : 
                     service.UsageCount <= 5 ? 'Utilizare mică' :
                     service.UsageCount <= 15 ? 'Utilizare medie' : 'Utilizare mare'}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Preț formatat</span>
                  <span className="text-sm font-medium text-gray-900">
                    {service.FormattedPrice}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">ID serviciu</span>
                  <span className="text-sm font-medium text-gray-900">
                    #{service.Id}
                  </span>
                </div>
              </div>
            </div>

            {/* Timestamps */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Informații Temporale
              </h3>
              
              <div className="space-y-4">
                <div>
                  <div className="flex items-center text-sm text-gray-600 mb-1">
                    <Calendar className="w-4 h-4 mr-2" />
                    Data creării
                  </div>
                  <p className="text-sm font-medium text-gray-900 ml-6">
                    {formatDate(service.CreatedAt)}
                  </p>
                </div>
                
                {service.UpdatedAt && (
                  <div>
                    <div className="flex items-center text-sm text-gray-600 mb-1">
                      <Calendar className="w-4 h-4 mr-2" />
                      Ultima actualizare
                    </div>
                    <p className="text-sm font-medium text-gray-900 ml-6">
                      {formatDate(service.UpdatedAt)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Acțiuni Rapide
              </h3>
              
              <div className="space-y-3">
                <button
                  onClick={handleEdit}
                  className="w-full bg-secondary-500 text-white py-2 px-4 rounded-lg hover:bg-secondary-600 transition-colors flex items-center justify-center"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editează Serviciul
                </button>
                
                <button
                  onClick={() => navigate('/contracts/new', { state: { preselectedService: service } })}
                  className="w-full bg-primary-500 text-white py-2 px-4 rounded-lg hover:bg-primary-600 transition-colors flex items-center justify-center"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Creează Contract
                </button>
                
                <button
                  onClick={() => navigate('/services')}
                  className="w-full bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Vezi Toate Serviciile
                </button>
              </div>
            </div>

            {/* Warning for deletion */}
            {service.UsageCount > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800 mb-1">
                      Atenție
                    </h4>
                    <p className="text-sm text-yellow-700">
                      Acest serviciu este folosit în {service.UsageCount} {service.UsageCount === 1 ? 'contract' : 'contracte'} 
                      și nu poate fi șters până când nu este eliminat din toate contractele.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                Confirmare Ștergere
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Sunteți sigur că doriți să ștergeți serviciul "{service.Name}"? 
              Această acțiune nu poate fi anulată.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Anulează
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-red-300 transition-colors flex items-center"
              >
                {deleteMutation.isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Se șterge...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Șterge Serviciul
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetailsPage;